import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, Sparkles, Bus, Zap, Crown, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 49,
    tagline: "Petites flottes & pilotes",
    accent: "bg-white",
    icon: Bus,
    features: [
      "Jusqu'à 5 véhicules suivis",
      "GTFS Morges/Rolle (mock ou réel)",
      "Carte live Leaflet/OSM",
      "Alertes retard règles déterministes",
      "1 lien client public LPD-safe",
      "Support email standard",
    ],
    cta: "Choisir Starter",
  },
  {
    id: "pro",
    name: "Pro",
    price: 79,
    tagline: "Le plus populaire",
    accent: "bg-[#FFC60A]",
    icon: Sparkles,
    highlight: true,
    features: [
      "Jusqu'à 20 véhicules suivis",
      "Recommandations IA (Claude / GPT)",
      "Notifications client illimitées",
      "Lien client public + multi-tenants",
      "Seuils de retard personnalisables",
      "Support prioritaire",
    ],
    cta: "Choisir Pro",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 149,
    tagline: "Réseaux & opérateurs",
    accent: "bg-zinc-900 text-white",
    icon: Crown,
    features: [
      "Véhicules illimités",
      "API Logitrak temps réel (clé fournie)",
      "Flux GTFS officiel CH (~500 MB)",
      "SLA 99.9 %, audit & logs",
      "Branding & sous-domaine dédié",
      "Account manager dédié",
    ],
    cta: "Contacter les ventes",
  },
];

export default function Pricing() {
  const [settings, setSettings] = useState(null);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    api.get("/logibus/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/tenants/me").then(({ data }) => setTenant(data)).catch(() => {});
  }, []);

  const choosePlan = async (plan) => {
    try {
      // For now we just register the intent; payment integration is P2.
      toast.success(`Demande enregistrée pour le forfait ${plan.name} (CHF ${plan.price}/mois)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const copyClientLink = () => {
    if (!settings) return;
    const url = `${window.location.origin}/client/bus-live/${settings.client_token}`;
    navigator.clipboard?.writeText(url);
    toast.success("Lien client copié");
  };

  return (
    <div className="p-4 lg:p-8" data-testid="pricing-page">
      <header className="mb-8">
        <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Premium · Logibus Live Delay</div>
        <h1 className="font-display font-black uppercase text-3xl lg:text-5xl tracking-tighter">Tarifs</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          Activez le module premium <strong>Suivi bus en live + Retards intelligents</strong> et offrez à vos passagers
          une vue publique simplifiée, conforme LPD, basée sur les données GTFS officielles de l'OFT.
        </p>
        {tenant && (
          <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
            <span className="px-2 py-0.5 border border-zinc-900 bg-zinc-100" data-testid="pricing-tenant">
              École : {tenant.name} · {tenant.code}
            </span>
            {settings?.client_token && (
              <button onClick={copyClientLink} data-testid="copy-client-link-pricing"
                className="px-2 py-0.5 border border-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1">
                <Copy size={10} /> Copier lien client
              </button>
            )}
            {settings?.client_token && (
              <a target="_blank" rel="noreferrer" href={`/client/bus-live/${settings.client_token}`}
                data-testid="open-client-view-pricing"
                className="px-2 py-0.5 border border-zinc-900 hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1">
                Aperçu vue client <ExternalLink size={10} />
              </a>
            )}
          </div>
        )}
      </header>

      <div className="grid md:grid-cols-3 gap-0 border border-zinc-900">
        {PLANS.map((p) => {
          const Icon = p.icon;
          return (
            <div
              key={p.id}
              data-testid={`plan-${p.id}`}
              className={`relative ${p.accent} border-r border-zinc-900 last:border-r-0 p-6 flex flex-col ${p.highlight ? "ring-4 ring-[#FFC60A] z-10" : ""}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-6 bg-zinc-900 text-[#FFC60A] text-[10px] font-mono tracking-widest uppercase px-2 py-1">
                  Recommandé
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="font-display font-black text-2xl tracking-tighter">{p.name}</div>
                <Icon size={22} />
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest opacity-70">{p.tagline}</div>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display font-black text-5xl tracking-tighter">CHF {p.price}</span>
                <span className="text-xs opacity-70">/mois</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={14} className={`mt-0.5 shrink-0 ${p.highlight ? "text-zinc-900" : p.id === "enterprise" ? "text-[#FFC60A]" : "text-zinc-900"}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`select-${p.id}`}
                onClick={() => choosePlan(p)}
                className={`mt-6 w-full py-3 border border-zinc-900 font-bold text-xs uppercase tracking-widest transition-colors ${
                  p.highlight
                    ? "bg-zinc-900 text-[#FFC60A] hover:bg-zinc-800"
                    : p.id === "enterprise"
                    ? "bg-[#FFC60A] text-zinc-900 hover:bg-[#ffd43a]"
                    : "bg-zinc-900 text-white hover:bg-zinc-800"
                }`}
              >
                {p.cta}
              </button>
            </div>
          );
        })}
      </div>

      <section className="mt-10 grid md:grid-cols-3 gap-4">
        <Insight icon={Zap} title="Activation immédiate" body="L'admin importe le GTFS Morges/Rolle en un clic, configure les seuils et obtient un lien client en 30 secondes." />
        <Insight icon={Sparkles} title="IA conforme LPD" body="Les recommandations sont générées via Emergent LLM Key (Claude/GPT) avec un fallback déterministe — aucune donnée passager n'est exposée." />
        <Insight icon={Bus} title="Migration progressive" body="Démarrez en Starter avec les stubs, basculez vers le flux GTFS officiel et l'API Logitrak quand vous êtes prêts." />
      </section>
    </div>
  );
}

function Insight({ icon: Icon, title, body }) {
  return (
    <div className="border border-zinc-900 bg-white p-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-[#FFC60A] border border-zinc-900 flex items-center justify-center">
          <Icon size={14} />
        </div>
        <div className="font-display font-bold text-sm uppercase tracking-widest">{title}</div>
      </div>
      <div className="mt-2 text-sm text-zinc-700">{body}</div>
    </div>
  );
}
